import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import * as yup from 'yup';

import { Radio } from 'components';
import { DatePickerButton, TimePicker } from 'design-system-web';
import { H5, H4 } from 'components/typography';
import { ServiceRequestScheduleType } from 'lane-shared/domains/workOrder/workOrderFeatureDefinitions';
import { parseDate } from 'async-timing';
import { UserContentInteractionContext } from 'lane-shared/contexts';

type TimeOption = {
  date: Date;
};

const INTERVAL_MINUTES = 15;
const MINUTES_IN_DAY = 24 * 60;

const SCHEDULE_OPTIONS = {
  ASAP: 'asap',
  SCHEDULED_DATE: 'scheduled-date',
} as const;

type ScheduleOption = (typeof SCHEDULE_OPTIONS)[keyof typeof SCHEDULE_OPTIONS];

type ServiceRequestScheduleProps = {
  onChange: (schedule: ServiceRequestScheduleType) => void;
  schedule: ServiceRequestScheduleType;
  timeZone?: string;
};

function generateTimeSlots(date: Date, timeZone?: string): TimeOption[] {
  const startOfDay = timeZone
    ? DateTime.fromJSDate(date).setZone(timeZone).startOf('day')
    : DateTime.fromJSDate(date).startOf('day');

  const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
  const isToday = startOfDay.hasSame(now, 'day');

  const slots: TimeOption[] = [];
  const totalSlots = MINUTES_IN_DAY / INTERVAL_MINUTES;

  for (let i = 0; i < totalSlots; i++) {
    const slotTime = startOfDay.plus({ minutes: INTERVAL_MINUTES * i });

    if (isToday && slotTime < now) continue;

    slots.push({ date: slotTime.toJSDate() });
  }

  return slots;
}

function getValidInitialTime(
  parsedDate: Date | null,
  timeZone?: string
): Date | null {
  if (!parsedDate) return null;

  const slots = generateTimeSlots(parsedDate, timeZone);
  const isValid = slots.some(
    slot => slot.date.getTime() === parsedDate.getTime()
  );

  return isValid ? parsedDate : null;
}

const invalidScheduleSchema = yup.object().shape({
  features: yup.object().shape({
    WorkOrder: yup.object().shape({
      Schedule: yup.object().shape({
        scheduledDueDate: yup
          .date()
          .required('Please select a date and time for your scheduled request')
          .typeError('Please select a date and time for your scheduled request'),
      }),
    }),
  }),
});

export const ServiceRequestSchedule = ({
  onChange,
  schedule,
  timeZone,
}: ServiceRequestScheduleProps) => {
  const { t } = useTranslation();
  const { validateAdditional } = useContext(UserContentInteractionContext);

  const parsedDate = parseDate(schedule?.scheduledDueDate);
  const isSpecificDate = parsedDate !== null;

  const [scheduleOption, setScheduleOption] = useState<ScheduleOption>(
    isSpecificDate ? SCHEDULE_OPTIONS.SCHEDULED_DATE : SCHEDULE_OPTIONS.ASAP
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(parsedDate);
  const [selectedTime, setSelectedTime] = useState<Date | null>(() =>
    getValidInitialTime(parsedDate, timeZone)
  );

  const isValid =
    scheduleOption === SCHEDULE_OPTIONS.ASAP || selectedTime !== null;

  useEffect(() => {
    if (validateAdditional) {
      validateAdditional(isValid ? null : invalidScheduleSchema);
    }

    return () => {
      if (validateAdditional) {
        validateAdditional(null);
      }
    };
  }, [isValid, validateAdditional]);

  const timeSlots = useMemo(
    () => (selectedDate ? generateTimeSlots(selectedDate, timeZone) : []),
    [selectedDate, timeZone]
  );

  const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
  const todaySlots = generateTimeSlots(now.toJSDate(), timeZone);

  const minDate =
    todaySlots.length === 0
      ? now.plus({ days: 1 }).startOf('day').toJSDate()
      : undefined;

  const handleScheduleOptionChange = (option: ScheduleOption) => {
    if (
      option === SCHEDULE_OPTIONS.ASAP ||
      option === SCHEDULE_OPTIONS.SCHEDULED_DATE
    ) {
      setScheduleOption(option);

      if (option === SCHEDULE_OPTIONS.ASAP) {
        onChange({ ...schedule, scheduledDueDate: null });
        setSelectedDate(null);
        setSelectedTime(null);
      } else {
        // Scheduled Date selected but no date/time yet = invalid
        onChange({ ...schedule, scheduledDueDate: undefined });
      }
    }
  };

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    onChange({ ...schedule, scheduledDueDate: undefined });
  };

  const handleTimeChange = (value: string) => {
    const date = new Date(value);

    setSelectedTime(date);
    onChange({ ...schedule, scheduledDueDate: date });
  };

  return (
    <div>
      <H4 mb={5}>{t('web.admin.serviceRequest.form.schedule.heading')}</H4>
      <H5 mb={3}>{t('web.admin.serviceRequest.form.schedule.label')}</H5>

      <div>
        <Radio<ScheduleOption>
          key={SCHEDULE_OPTIONS.ASAP}
          value={SCHEDULE_OPTIONS.ASAP}
          name="schedule"
          text={t('web.admin.serviceRequest.form.schedule.asap')}
          selected={scheduleOption}
          onChange={handleScheduleOptionChange}
          testId="asap"
        />
        <Radio<ScheduleOption>
          key={SCHEDULE_OPTIONS.SCHEDULED_DATE}
          value={SCHEDULE_OPTIONS.SCHEDULED_DATE}
          name="schedule"
          text={t('web.admin.serviceRequest.form.schedule.specific-date')}
          selected={scheduleOption}
          onChange={handleScheduleOptionChange}
          testId="specific-date"
        />
      </div>

      {scheduleOption === SCHEDULE_OPTIONS.SCHEDULED_DATE && (
        <div>
          <label data-test="date-requested">
            {t('web.admin.serviceRequest.form.schedule.date-requested')}
          </label>
          <div style={{ marginTop: '10px', display: 'flex', gap: '12px' }}>
            <DatePickerButton
              value={selectedDate}
              onChange={handleDateChange}
              dateInputLabel={t(
                'web.admin.serviceRequest.form.schedule.date-input-label'
              )}
              hideOnSelect
              disablePastDays
              minDate={minDate}
              timeZone={timeZone}
            />
            <TimePicker
              value={selectedTime}
              onChange={handleTimeChange}
              times={timeSlots}
              timeZone={timeZone}
              label={t(
                'web.admin.serviceRequest.form.schedule.time-input-label'
              )}
              showClear={false}
              showTimezone={false}
              disabled={!selectedDate}
            />
          </div>
        </div>
      )}
    </div>
  );
};
